import { supabase } from '../lib/supabase';
import { HistoryRecord, AgeGroup } from '../types/domain';
import { Database } from '../types/supabase';

type InjectionHistoryInsert = Database['public']['Tables']['injection_history']['Insert'];

const LAST_SITE_KEY_KIDS = 'lastSite_2_10';
const LAST_SITE_KEY_TEEN = 'lastSite_11_plus';
const HISTORY_KEY_KIDS = 'history_2_10';
const HISTORY_KEY_TEEN = 'history_11_plus';

const CACHE_HISTORY_PREFIX = 'sb_cache_history_';
const CACHE_LAST_SITE_PREFIX = 'sb_cache_last_site_';
const OFFLINE_QUEUE_PREFIX = 'sb_offline_queue_';

interface OfflineAction {
  type: 'add_injection' | 'update_last_site';
  payload: any;
  timestamp: number;
}

export const dataService = {
  async migrateLocalData(userId: string) {
    const kidsHistoryRaw = localStorage.getItem(HISTORY_KEY_KIDS);
    const teenHistoryRaw = localStorage.getItem(HISTORY_KEY_TEEN);
    const kidsLastSite = localStorage.getItem(LAST_SITE_KEY_KIDS);
    const teenLastSite = localStorage.getItem(LAST_SITE_KEY_TEEN);

    if (!kidsHistoryRaw && !teenHistoryRaw && !kidsLastSite && !teenLastSite) {
      return;
    }

    const recordsToInsert: InjectionHistoryInsert[] = [];

    if (kidsHistoryRaw) {
      try {
        const parsed = JSON.parse(kidsHistoryRaw);
        if (Array.isArray(parsed)) {
          parsed.forEach((entry: any) => {
             recordsToInsert.push({
               user_id: userId,
               date: entry.date,
               time: entry.time,
               site_id: entry.siteId,
               insulin_type_id: entry.insulinTypeId,
               age_group: '2-10'
             });
          });
        }
      } catch (e) { console.error(e); }
    }

    if (teenHistoryRaw) {
      try {
        const parsed = JSON.parse(teenHistoryRaw);
        if (Array.isArray(parsed)) {
          parsed.forEach((entry: any) => {
             recordsToInsert.push({
               user_id: userId,
               date: entry.date,
               time: entry.time,
               site_id: entry.siteId,
               insulin_type_id: entry.insulinTypeId,
               age_group: '11-plus'
             });
          });
        }
      } catch (e) { console.error(e); }
    }

    if (recordsToInsert.length > 0) {
      const { error } = await supabase.from('injection_history').insert(recordsToInsert);
      if (error) console.error('Migration history error:', error);
    }

    const { data: existingLastSiteData } = await supabase
        .from('last_injection_site')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

    const existingLastSite = existingLastSiteData as any;

    const lastSiteData: any = {
        user_id: userId,
        updated_at: new Date().toISOString(),
        last_site_id_kids: existingLastSite?.last_site_id_kids ?? (kidsLastSite ? parseInt(kidsLastSite) : null),
        last_site_id_teen: existingLastSite?.last_site_id_teen ?? (teenLastSite ? parseInt(teenLastSite) : null)
    };

    const { error: lastSiteError } = await supabase.from('last_injection_site').upsert(lastSiteData);

    if (lastSiteError) console.error('Migration last site error:', lastSiteError);

    localStorage.removeItem(HISTORY_KEY_KIDS);
    localStorage.removeItem(HISTORY_KEY_TEEN);
    localStorage.removeItem(LAST_SITE_KEY_KIDS);
    localStorage.removeItem(LAST_SITE_KEY_TEEN);
  },

  async getHistory(userId: string): Promise<HistoryRecord[]> {
    try {
      const { data, error } = await supabase
        .from('injection_history')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false })
        .order('time', { ascending: false });

      if (error) throw error;

      // Update cache
      localStorage.setItem(CACHE_HISTORY_PREFIX + userId, JSON.stringify(data));

      return data.map((row: any) => ({
        date: row.date,
        time: row.time,
        siteId: row.site_id,
        insulinTypeId: row.insulin_type_id,
        ageGroup: row.age_group as AgeGroup,
        insulinBrand: row.insulin_brand,
        insulinDose: row.insulin_dose,
        glucoseValue: row.glucose_value
      }));
    } catch (err) {
      console.warn('Offline mode or error fetching history, using cache:', err);
      const cached = localStorage.getItem(CACHE_HISTORY_PREFIX + userId);
      if (cached) {
        try {
          const data = JSON.parse(cached);
          return data.map((row: any) => ({
            date: row.date,
            time: row.time,
            siteId: row.site_id,
            insulinTypeId: row.insulin_type_id,
            ageGroup: row.age_group as AgeGroup,
            insulinBrand: row.insulin_brand,
            insulinDose: row.insulin_dose,
            glucoseValue: row.glucose_value
          }));
        } catch {
          return [];
        }
      }
      return [];
    }
  },

  async addInjection(userId: string, record: HistoryRecord) {
    const payload = {
      user_id: userId,
      date: record.date,
      time: record.time,
      site_id: record.siteId,
      insulin_type_id: record.insulinTypeId,
      age_group: record.ageGroup,
      insulin_brand: record.insulinBrand,
      insulin_dose: record.insulinDose,
      glucose_value: record.glucoseValue
    };

    try {
      const { error } = await supabase.from('injection_history').insert(payload);
      if (error) throw error;

      // Update local cache for history implicitly?
      // It's better to let getHistory handle cache updates, but we can append to cache to be responsive.
      const cached = localStorage.getItem(CACHE_HISTORY_PREFIX + userId);
      if (cached) {
        const data = JSON.parse(cached);
        data.unshift({
            ...payload,
            site_id: payload.site_id,
            insulin_type_id: payload.insulin_type_id,
            age_group: payload.age_group,
            insulin_brand: payload.insulin_brand,
            insulin_dose: payload.insulin_dose,
            glucose_value: payload.glucose_value
        }); // Prepend new record
        localStorage.setItem(CACHE_HISTORY_PREFIX + userId, JSON.stringify(data));
      }

    } catch (err) {
      console.warn('Offline mode or error saving injection, queuing:', err);
      this.queueOfflineAction(userId, {
        type: 'add_injection',
        payload,
        timestamp: Date.now()
      });

      // Optimistic update for history cache
      const cached = localStorage.getItem(CACHE_HISTORY_PREFIX + userId);
      const data = cached ? JSON.parse(cached) : [];
      data.unshift({
        ...payload,
        site_id: payload.site_id,
        insulin_type_id: payload.insulin_type_id,
        age_group: payload.age_group,
        insulin_brand: payload.insulin_brand,
        insulin_dose: payload.insulin_dose,
        glucose_value: payload.glucose_value
      });
      localStorage.setItem(CACHE_HISTORY_PREFIX + userId, JSON.stringify(data));
    }
  },

  async updateLastSite(userId: string, ageGroup: AgeGroup, siteId: number) {
    let currentData: any = null;

    try {
      const { data, error } = await supabase.from('last_injection_site').select('*').eq('user_id', userId).single();
      if (!error) currentData = data;
    } catch {}

    if (!currentData) {
       // Try cache
       const cached = localStorage.getItem(CACHE_LAST_SITE_PREFIX + userId);
       if (cached) currentData = JSON.parse(cached);
    }

    const updateData: any = {
        user_id: userId,
        updated_at: new Date().toISOString(),
        last_site_id_kids: currentData?.last_site_id_kids,
        last_site_id_teen: currentData?.last_site_id_teen
    };

    if (ageGroup === '2-10') {
        updateData.last_site_id_kids = siteId;
    } else {
        updateData.last_site_id_teen = siteId;
    }

    try {
        const { error } = await supabase.from('last_injection_site').upsert(updateData);
        if (error) throw error;
        // Update cache on success
        localStorage.setItem(CACHE_LAST_SITE_PREFIX + userId, JSON.stringify(updateData));
    } catch (err) {
        console.warn('Offline mode or error updating last site, queuing:', err);
        this.queueOfflineAction(userId, {
            type: 'update_last_site',
            payload: updateData,
            timestamp: Date.now()
        });
        // Optimistic cache update
        localStorage.setItem(CACHE_LAST_SITE_PREFIX + userId, JSON.stringify(updateData));
    }
  },

  async getLastSite(userId: string, ageGroup: AgeGroup): Promise<number | null> {
    try {
      const { data, error } = await supabase
        .from('last_injection_site')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) throw error;

      const siteData = data as any;
      localStorage.setItem(CACHE_LAST_SITE_PREFIX + userId, JSON.stringify(siteData));

      if (ageGroup === '2-10') return siteData.last_site_id_kids;
      return siteData.last_site_id_teen;
    } catch (err) {
      console.warn('Offline mode or error fetching last site, using cache:', err);
      const cached = localStorage.getItem(CACHE_LAST_SITE_PREFIX + userId);
      if (cached) {
        try {
          const data = JSON.parse(cached);
          if (ageGroup === '2-10') return data.last_site_id_kids;
          return data.last_site_id_teen;
        } catch {
          return null;
        }
      }
      return null;
    }
  },

  queueOfflineAction(userId: string, action: OfflineAction) {
    const key = OFFLINE_QUEUE_PREFIX + userId;
    const queueRaw = localStorage.getItem(key);
    const queue: OfflineAction[] = queueRaw ? JSON.parse(queueRaw) : [];
    queue.push(action);
    localStorage.setItem(key, JSON.stringify(queue));
  },

  async syncOfflineData(userId: string) {
    const key = OFFLINE_QUEUE_PREFIX + userId;
    const queueRaw = localStorage.getItem(key);
    if (!queueRaw) return;

    const queue: OfflineAction[] = JSON.parse(queueRaw);
    if (queue.length === 0) return;

    console.log(`Syncing ${queue.length} offline actions...`);

    const newQueue: OfflineAction[] = [];

    for (const action of queue) {
      try {
        if (action.type === 'add_injection') {
          const { error } = await supabase.from('injection_history').insert(action.payload as any);
          if (error) throw error;
        } else if (action.type === 'update_last_site') {
           const { error } = await supabase.from('last_injection_site').upsert(action.payload as any);
           if (error) throw error;
        }
      } catch (err) {
        console.error('Failed to sync action:', action, err);
        newQueue.push(action); // Keep in queue if failed
      }
    }

    if (newQueue.length > 0) {
      localStorage.setItem(key, JSON.stringify(newQueue));
    } else {
      localStorage.removeItem(key);
    }
  }
};
